/**
 * HVP Cascade Controller - dropdown change handling
 *
 * The Smart Cascade change path plus the shared selection-changed emitter.
 * Includes the upstream self-heal that adopts DOM values missing from state
 * (browser back/forward restores selects without firing change) BEFORE any
 * cascade logic runs.
 *
 * Split out of cascade-controller.js (350-line rule): the methods below moved
 * VERBATIM and are installed on HVPCascadeController.prototype by
 * cascade-controller.js, so `this` is the same instance and behavior is identical.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

export const CascadeChangeMethods = {
    /**
     * Handle dropdown change with Smart Cascade logic
     *
     * Smart Cascade Flow:
     * 1. Update the selection for the changed attribute
     * 2. Check if selections BELOW this index are still compatible
     * 3. If compatible: Keep below selections, just update the variation
     * 4. If NOT compatible: Clear subsequent dropdowns and load new options
     *
     * This preserves user selections when changing upper-level attributes
     * if the combination remains valid, improving UX significantly.
     *
     * @param {Event} event - Change event from dropdown
     * @fires selectionChanged - When any selection changes
     * @fires compatibilityChecked - After compatibility check completes
     * @fires hvp_selection_changed - DOM event for external listeners
     */
    async handleDropdownChange(event) {
        const select = event.target;
        const attrName = select.dataset.attribute_name;
        const value = select.value;

        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ----------------------------------------');
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] DROPDOWN CHANGE TRIGGERED');
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ----------------------------------------');
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Attribute:', attrName);
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Value:', value || '(empty/cleared)');
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Smart Cascade Enabled:', this.smartCascadeEnabled);

        if (!attrName) return;

        const normalized = this.normalizeAttributeName(attrName);
        const previousValue = this.selections[normalized];

        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Normalized attribute:', normalized);
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Previous value:', previousValue || '(none)');

        // Update selections
        if (value) {
            this.selections[normalized] = value;
        } else {
            delete this.selections[normalized];
        }

        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Current selections:', JSON.stringify(this.selections));
        this.log('Selection changed:', normalized, '=', value || '(cleared)');

        // Get attribute index
        const index = this.getAttributeIndex(normalized);
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Attribute index in order:', index);
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Total attributes:', this.attributeOrder.length);

        if (index === -1) {
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ERROR: Attribute not found in order');
            this.log('Attribute not in order:', normalized);
            return;
        }

        // Self-heal: adopt any UPSTREAM dropdown value visible in the DOM but
        // missing from state (browser back/forward restoration fills selects
        // without change events). Only restoration produces this divergence —
        // every scripted writer (TAV, smart-hide, carry-back) fires change —
        // so adopting is safe and makes the cascade compute with what the
        // customer actually sees selected.
        for (let i = 0; i < index; i++) {
            const upstream = this.normalizeAttributeName(this.attributeOrder[i]);
            const upstreamSelect = this.dropdowns.get(upstream);
            if (upstreamSelect && upstreamSelect.value && !this.selections[upstream]) {
                this.selections[upstream] = upstreamSelect.value;
                this.log('Adopted restored upstream selection:', upstream, '=', upstreamSelect.value);
            }
        }

        const isLastAttribute = (index === this.attributeOrder.length - 1);
        const belowSelections = this.getSelectionsBelow(index);
        const hasBelowSelections = Object.keys(belowSelections).length > 0;

        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Is last attribute:', isLastAttribute);
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Has below selections:', hasBelowSelections);

        // SMART CASCADE: Check if current selections (with new value) match any variation
        // This applies to ALL attributes, not just non-last ones
        if (this.smartCascadeEnabled && value) {
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] *** SMART CASCADE MODE ***');
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Below selections:', JSON.stringify(belowSelections));

            // STEP 1: If we have below selections, check if full combination is valid
            if (hasBelowSelections) {
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] SMART CASCADE: Checking compatibility with below selections');
                this.log('Smart Cascade: Checking compatibility with below selections', belowSelections);

                const compatResult = await this.checkCompatibilityWithBelow(index, value);

                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] SMART CASCADE: Compatibility result:', {
                    isCompatible: compatResult.isCompatible,
                    hasVariation: !!compatResult.variation,
                    variationId: compatResult.variation?.variation_id || 'none'
                });

                this.emit('compatibilityChecked', {
                    attribute: normalized,
                    value: value,
                    isCompatible: compatResult.isCompatible,
                    variation: compatResult.variation
                });

                if (compatResult.isCompatible) {
                    // Below selections are still valid - keep them!
                    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] SMART CASCADE: COMPATIBLE - Preserving below selections!');
                    this.log('Smart Cascade: Below selections compatible, preserving');

                    // Update variation if we have a complete match
                    if (compatResult.variation) {
                        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] SMART CASCADE: Variation found:', compatResult.variation.variation_id);
                        this.currentVariation = compatResult.variation;
                        this.applyVariation(compatResult.variation);

                        this.emit('variationFound', {
                            variation: compatResult.variation,
                            preservedSelections: true
                        });

                        this.fireCustomEvent('hvp_variation_found', {
                            variation: compatResult.variation,
                            preservedSelections: true
                        });
                    }

                    // CRITICAL FIX: Still update dropdown OPTIONS for downstream attributes
                    // Even though we preserve the selected VALUES, the OPTIONS need to be filtered
                    // to show only valid choices based on the new upstream selection
                    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] SMART CASCADE: Updating downstream dropdown OPTIONS (preserving values)');
                    await this.updateDownstreamDropdownOptions(index);

                    // Schedule UI update for highlighting
                    this.scheduler.scheduleUIUpdate('highlight', 'selection-change');
                    this.scheduler.scheduleUIUpdate('progress', 'selection-change');

                    // Emit selection changed event
                    this.emitSelectionChanged(normalized, value);
                    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] SMART CASCADE: Complete - selections preserved, options updated');
                    return;
                }

                // Not compatible - proceed with traditional cascade
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] SMART CASCADE: INCOMPATIBLE - Falling back to traditional cascade');
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Clearing attributes:', Object.keys(belowSelections));
                this.log('Smart Cascade: Below selections incompatible, clearing');

                this.emit('selectionsCleared', {
                    reason: 'incompatible',
                    clearedAttributes: Object.keys(belowSelections),
                    changedAttribute: normalized,
                    newValue: value
                });
            }
            // STEP 2: If this is the last attribute and no below selections,
            // just try to find variation with current selections (no clearing needed)
            else if (isLastAttribute) {
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] SMART CASCADE: Last attribute - checking for variation match');

                // FIX: Filter last attribute's options based on upstream selections
                // This handles the edge case where all attributes are pre-selected
                if (this.clientCascade && this.clientCascade.isAvailable()) {
                    const upstreamSelections = {};
                    for (let i = 0; i < index; i++) {
                        const upstreamAttr = this.attributeOrder[i];
                        const upstreamNormalized = this.normalizeAttributeName(upstreamAttr);
                        if (this.selections[upstreamNormalized]) {
                            upstreamSelections[upstreamNormalized] = this.selections[upstreamNormalized];
                        }
                    }

                    const filteredOptions = this.clientCascade.getOptions(upstreamSelections, normalized);
                    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Last attribute filtered options:', filteredOptions);
                    this.updateDropdownOptions(normalized, filteredOptions);
                    this.fireLegacyUpdatedEvent(['attribute_' + normalized]);
                }

                if (this.areAllSelectionsComplete()) {
                    await this.findMatchingVariation();
                }

                // Schedule UI update
                this.scheduler.scheduleUIUpdate('highlight', 'selection-change');
                this.scheduler.scheduleUIUpdate('progress', 'selection-change');

                this.emitSelectionChanged(normalized, value);
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] SMART CASCADE: Last attribute handled - no cascade needed');
                return;
            } else {
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] SMART CASCADE: No below selections - proceeding with standard cascade');
            }
        } else {
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] *** TRADITIONAL CASCADE MODE ***');
            if (!this.smartCascadeEnabled) {
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Reason: Smart Cascade is disabled');
            } else if (!value) {
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Reason: No value selected (clearing)');
            }
        }

        // Traditional cascade: Clear subsequent dropdowns (only if not the last attribute)
        if (!isLastAttribute) {
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Clearing subsequent dropdowns from index:', index + 1);
            this.clearSubsequentDropdowns(index);

            // Load next options
            if (value) {
                await this.loadBatchOptions();
                await this.autoSelectSingleOptions(index + 1);
            }
        } else {
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Last attribute - no subsequent dropdowns to clear');
        }

        // Check for complete selection
        if (this.areAllSelectionsComplete()) {
            await this.findMatchingVariation();
        }

        // Schedule UI update
        this.scheduler.scheduleUIUpdate('highlight', 'selection-change');
        this.scheduler.scheduleUIUpdate('progress', 'selection-change');

        // Emit selection changed event
        this.emitSelectionChanged(normalized, value);
    },

    /**
     * Emit selection changed events (helper to avoid duplication)
     *
     * @private
     * @param {string} attribute - Normalized attribute name
     * @param {string} value - Selected value
     */
    emitSelectionChanged(attribute, value) {
        // Invariant: a resolved variation must reflect a COMPLETE selection.
        // If the selection is no longer complete (e.g. the user changed an
        // upstream attribute, which clears the downstream dropdowns), any
        // previously-applied variation is stale and MUST be cleared — otherwise
        // the form keeps showing that variation's price/SKU and, critically,
        // leaves its variation_id in the hidden cart input, so an incomplete
        // selection could add the wrong variation to the cart. clearVariation()
        // blanks price/availability/SKU, hides .single_variation, disables
        // add-to-cart, and empties the variation_id input.
        if (this.currentVariation && !this.areAllSelectionsComplete()) {
            this.currentVariation = null;
            this.clearVariation();
        }

        this.emit('selectionChanged', {
            attribute: attribute,
            value: value,
            selections: { ...this.selections }
        });

        this.fireCustomEvent('hvp_selection_changed', {
            attribute: attribute,
            value: value,
            selections: { ...this.selections },
            // Lets passive listeners (e.g. the no-match notice) know whether the
            // selection is now complete. Used to clear a stale "unavailable"
            // message once the user backs out to an incomplete state. On the
            // no-match path this fires AFTER hvp_no_match with complete:true,
            // so the notice is preserved.
            complete: this.areAllSelectionsComplete()
        });
    },
};

export default CascadeChangeMethods;
